/**
 * @file Contributor dashboard UI — pages/Dashboard/Contributor/Stages/A3/utils/a3ApiFormPrefill.ts.
 */

import type {
  FormDataResponse,
  FormField,
  FormSubSection,
} from "../../../../../../interfaces/formInterfaces";
import {
  isA3EmbodiedCarbonLockedManufacturingProcessField,
  isA3EmbodiedEnergyLockedManufacturingProcessField,
  isA3FuelLockedManufacturingProcessField,
} from "../../../../../../utils/a3ManufacturingProcessResolution";
import { buildA3FuelLoadLayout } from "./a3ProcessFuelLayout";
import { mapA3ProcessApiRowToRawMaterialFormValues } from "./a3ProcessRawMaterials";
import {
  discoverA3CarbonFunctionalAndRawMaterialQtyFields,
  discoverA3CarbonFunctionalUnitField,
} from "./a3EmbodiedCarbonFromFuels";
import { discoverA3CarbonCharacterizationFields } from "./a3CarbonCharacterizationFactor";
import { discoverA3EmbodiedEnergyValueAndUnitFields } from "./a3FuelEmbodiedEnergyKwh";
import { discoverA3FuelFieldNames } from "./a3FuelMixAutofill";
import {
  applyA3PlantMaterialQualityValuesToForm,
  isA3PlantMaterialQualityField,
} from "../../../../../../utils/a3RawMaterialQualityField";
import { formatEmbodiedCarbonReferenceQtyFromUnknown, formatEmbodiedCarbonFunctionalUnitFromUnknown } from "../../shared/embodiedCarbonReferenceQtyFormat";

function s(v: unknown): string {
  if (v === null || v === undefined) return "";
  if (typeof v === "object") return "";
  return String(v).trim();
}

function formatA3ReferenceEmbodiedCarbonQty(value: unknown): string {
  return formatEmbodiedCarbonReferenceQtyFromUnknown(value);
}

function formatA3FunctionalUnitCarbonQty(value: unknown): string {
  return formatEmbodiedCarbonFunctionalUnitFromUnknown(value);
}

/** Committed submissions use Prisma columns; bulk-import previews use CMS field names. */
export function readA3ApiValueForField(
  sectionCode: string,
  field: FormField,
  row: Record<string, unknown>
): string {
  const direct = row[field.name];
  if (direct !== undefined && direct !== null && direct !== "") {
    return s(direct);
  }

  const fname = field.name;
  const fn = fname.toLowerCase();

  if (sectionCode === "A3_MANUFACTURING_PLANT_DETAILS") {
    if (field.type === "PLANT_CAPACITY" || fname === "plantCapacity_A3") {
      const id = row.plantCapacityId ?? row.plantCapacity;
      if (id != null && id !== "") return s(id);
      const opt = row.plantCapacityOption as Record<string, unknown> | undefined;
      if (opt?.id != null) return s(opt.id);
    }
    const plantMap: Record<string, string> = {
      materialQuality_A3: "materialQualityId",
      plantTemperature_A3: "plantStandardOperationTemperature",
      plantTemperatureUnit_A3: "temperatureUnitId",
      plantPressure_A3: "plantStandardOperationPressure",
      plantPressureUnit_A3: "pressureUnitId",
    };
    const col = plantMap[fname];
    if (col && row[col] != null) return s(row[col]);
  }

  if (sectionCode === "A3_PROCESS_DETAILS") {
    if (field.type === "RAW_MATERIALS") return "";
    if (
      fname === "processName_A3_01" ||
      field.type === "MANUFACTURING_PROCESS" ||
      fn.includes("processname")
    ) {
      return s(row.processName);
    }
  }

  if (sectionCode === "A3_PROCESS_FUEL_DETAILS") {
    if (isA3FuelLockedManufacturingProcessField(field, sectionCode)) {
      return "";
    }
    const fuelMap: Record<string, string> = {
      fuelMix: "fuelMix",
      fuelQuantity: "fuelQuantity",
      fuelQuantityUnit: "fuelQuantityUnitId",
      fuelDensity: "fuelDensity",
      fuelDensityUnit: "fuelDensityUnitId",
      calorificValue: "calorificValue",
      calorificValueUnit: "calorificValueUnitId",
    };
    const names = discoverA3FuelFieldNames(null);
    const resolvedNames = { ...names };
    if (fname === resolvedNames.mix || fn.includes("fuelmix")) {
      return s(row.fuelMix);
    }
    if (
      fname === resolvedNames.quantity ||
      (fn.includes("fuelquantity") && !fn.includes("unit"))
    ) {
      return s(row.fuelQuantity);
    }
    if (fname === resolvedNames.quantityUnit || fn.includes("fuelquantityunit")) {
      return s(row.fuelQuantityUnitId);
    }
    if (
      fname === resolvedNames.density ||
      (fn.includes("fueldensity") && !fn.includes("unit"))
    ) {
      return s(row.fuelDensity);
    }
    if (fname === resolvedNames.densityUnit || fn.includes("fueldensityunit")) {
      return s(row.fuelDensityUnitId);
    }
    if (fname === resolvedNames.calorific || fn.includes("calorificvalue")) {
      return s(row.calorificValue);
    }
    if (
      fname === resolvedNames.calorificUnit ||
      (fn.includes("calorific") && fn.includes("unit"))
    ) {
      return s(row.calorificValueUnitId);
    }
    for (const [cmsKey, col] of Object.entries(fuelMap)) {
      if (fn.includes(cmsKey.toLowerCase()) && row[col] != null) {
        return s(row[col]);
      }
    }
  }

  if (sectionCode === "A3_EMBODIED_ENERGY_DETAILS") {
    if (isA3EmbodiedEnergyLockedManufacturingProcessField(field, sectionCode)) {
      return "";
    }
    const energyFields = discoverA3EmbodiedEnergyValueAndUnitFields(null);
    if (energyFields?.valueField && fname === energyFields.valueField) {
      return s(row.embodiedEnergy);
    }
    if (energyFields?.unitField && fname === energyFields.unitField) {
      return s(row.unitId);
    }
    if (fn.includes("embodiedenergy") && fn.includes("unit")) {
      return s(row.unitId);
    }
    if (fn.includes("embodiedenergy")) {
      return s(row.embodiedEnergy);
    }
  }

  if (sectionCode === "A3_EMBODIED_CARBON_DETAILS") {
    if (isA3EmbodiedCarbonLockedManufacturingProcessField(field, sectionCode)) {
      return "";
    }
    const cf = discoverA3CarbonCharacterizationFields(null);
    const qty = discoverA3CarbonFunctionalAndRawMaterialQtyFields(null);
    const fu = discoverA3CarbonFunctionalUnitField(null);
    if (cf?.valueField && fname === cf.valueField) {
      return s(row.characterizationFactor);
    }
    if (cf?.unitField && fname === cf.unitField) {
      return s(row.characterizationFactorUnit);
    }
    if (qty?.referenceEmbodiedField && fname === qty.referenceEmbodiedField) {
      return formatA3ReferenceEmbodiedCarbonQty(row.referenceQty);
    }
    if (qty?.rawMaterialQtyField && fname === qty.rawMaterialQtyField) {
      return formatA3ReferenceEmbodiedCarbonQty(row.referenceQty);
    }
    if (fn.includes("rawmaterial") && fn.includes("unit")) {
      return s(row.referenceQtyUnitId);
    }
    if (qty?.functionalField && fname === qty.functionalField) {
      return formatA3FunctionalUnitCarbonQty(row.functionalUnitCarbon);
    }
    if (fname === fu || (fn.includes("functional") && fn.includes("unit"))) {
      return s(row.functionalUnitCarbonUnitId);
    }
  }

  return s(row[fname]);
}

/** Exported function — apply A3Background Material Labels. */
export function applyA3BackgroundMaterialLabels(
  nextValues: Record<string, string>,
  submission: Record<string, unknown>
): void {
  const bg = submission.background as Record<string, unknown> | undefined;
  if (!bg) return;

  const material = bg.material as Record<string, unknown> | undefined;
  const subMaterial = bg.subMaterial as Record<string, unknown> | undefined;

  nextValues.material_A3 =
    bg.materialId != null && String(bg.materialId).trim() !== ""
      ? String(bg.materialId)
      : "";
  nextValues.material_A3_label =
    s(material?.name) || s(bg.materialCode) || s(material?.code);

  nextValues.subMaterial_A3 =
    bg.subMaterialId != null && String(bg.subMaterialId).trim() !== ""
      ? String(bg.subMaterialId)
      : "";
  nextValues.subMaterial_A3_label =
    s(subMaterial?.name) || s(bg.subMaterialUnit);
}

/** Exported function — build A3Values From Submission. */
export function buildA3ValuesFromSubmission(params: {
  sections: FormSubSection[];
  submission: Record<string, unknown>;
  backgroundRawMaterialOptions: Array<{ id: number; name: string }>;
  formData?: FormDataResponse | null;
}): {
  values: Record<string, string>;
  energyTotal: number;
  carbonTotal: number;
} {
  const { sections, submission, backgroundRawMaterialOptions, formData } =
    params;
  const nextValues: Record<string, string> = {};
  let energyTotal = 0;
  let carbonTotal = 0;

  applyA3BackgroundMaterialLabels(nextValues, submission);

  const fuelNames = discoverA3FuelFieldNames(formData ?? null);
  const energyFields = discoverA3EmbodiedEnergyValueAndUnitFields(formData ?? null);
  const carbonCf = discoverA3CarbonCharacterizationFields(formData ?? null);
  const carbonQty = discoverA3CarbonFunctionalAndRawMaterialQtyFields(
    formData ?? null
  );
  const carbonFu = discoverA3CarbonFunctionalUnitField(formData ?? null);

  const readField = (
    sectionCode: string,
    field: FormField,
    row: Record<string, unknown>
  ): string => {
    if (sectionCode === "A3_PROCESS_FUEL_DETAILS") {
      const fname = field.name;
      const fn = fname.toLowerCase();
      if (isA3FuelLockedManufacturingProcessField(field, sectionCode)) {
        return "";
      }
      if (fname === fuelNames.mix || fn.includes("fuelmix")) {
        return s(row.fuelMix);
      }
      if (
        fname === fuelNames.quantity ||
        (fn.includes("fuelquantity") && !fn.includes("unit"))
      ) {
        return s(row.fuelQuantity);
      }
      if (fname === fuelNames.quantityUnit) return s(row.fuelQuantityUnitId);
      if (
        fname === fuelNames.density ||
        (fn.includes("fueldensity") && !fn.includes("unit"))
      ) {
        return s(row.fuelDensity);
      }
      if (fname === fuelNames.densityUnit) return s(row.fuelDensityUnitId);
      if (fname === fuelNames.calorific || fn.includes("calorificvalue")) {
        return s(row.calorificValue);
      }
      if (fname === fuelNames.calorificUnit) return s(row.calorificValueUnitId);
    }

    if (sectionCode === "A3_EMBODIED_ENERGY_DETAILS") {
      if (isA3EmbodiedEnergyLockedManufacturingProcessField(field, sectionCode)) {
        return "";
      }
      if (energyFields?.valueField && field.name === energyFields.valueField) {
        return s(row.embodiedEnergy);
      }
      if (energyFields?.unitField && field.name === energyFields.unitField) {
        return s(row.unitId);
      }
    }

    if (sectionCode === "A3_EMBODIED_CARBON_DETAILS") {
      if (isA3EmbodiedCarbonLockedManufacturingProcessField(field, sectionCode)) {
        return "";
      }
      if (carbonCf?.valueField && field.name === carbonCf.valueField) {
        return s(row.characterizationFactor);
      }
      if (carbonCf?.unitField && field.name === carbonCf.unitField) {
        return s(row.characterizationFactorUnit);
      }
      if (
        carbonQty?.referenceEmbodiedField &&
        field.name === carbonQty.referenceEmbodiedField
      ) {
        return formatA3ReferenceEmbodiedCarbonQty(row.referenceQty);
      }
      if (
        carbonQty?.rawMaterialQtyField &&
        field.name === carbonQty.rawMaterialQtyField
      ) {
        return formatA3ReferenceEmbodiedCarbonQty(row.referenceQty);
      }
      if (
        field.name.toLowerCase().includes("rawmaterial") &&
        field.name.toLowerCase().includes("unit")
      ) {
        return s(row.referenceQtyUnitId);
      }
      if (carbonQty?.functionalField && field.name === carbonQty.functionalField) {
        return formatA3FunctionalUnitCarbonQty(row.functionalUnitCarbon);
      }
      if (
        field.name === carbonFu ||
        (field.name.toLowerCase().includes("functional") &&
          field.name.toLowerCase().includes("unit"))
      ) {
        return s(row.functionalUnitCarbonUnitId);
      }
    }

    return readA3ApiValueForField(sectionCode, field, row);
  };

  sections.forEach((sec) => {
    const code = sec.code!;
    let source: unknown[] = [];

    if (code === "A3_PROCESS_DETAILS") {
      source = (submission.A3_Process as unknown[]) || [];
    } else if (code === "A3_MANUFACTURING_PLANT_DETAILS") {
      source = (submission.A3_Plant as unknown[]) || [];
    } else if (code === "A3_EMBODIED_ENERGY_DETAILS") {
      source = (submission.A3_Energy as unknown[]) || [];
    } else if (code === "A3_EMBODIED_CARBON_DETAILS") {
      source = (submission.A3_Carbon as unknown[]) || [];
    } else if (code === "A3_PROCESS_FUEL_DETAILS") {
      const { flatItems } = buildA3FuelLoadLayout(
        submission.A3_Process as unknown[] | undefined,
        submission.A3_Fuel as unknown[] | undefined
      );
      flatItems.forEach((row, rowIndex) => {
        const item = (row ?? {}) as Record<string, unknown>;
        sec.fields.forEach((f) => {
          const key = `${f.name}_${code}_row${rowIndex}`;
          const value = readField(code, f, item);
          if (value !== "") nextValues[key] = value;
        });
      });
      return;
    }

    source.forEach((rawRow, rowIndex) => {
      const row = (rawRow ?? {}) as Record<string, unknown>;

      sec.fields.forEach((f) => {
        const key = `${f.name}_${code}_row${rowIndex}`;

        if (code === "A3_PROCESS_DETAILS" && f.type === "RAW_MATERIALS") {
          const mapped = mapA3ProcessApiRowToRawMaterialFormValues(
            row,
            backgroundRawMaterialOptions
          );
          nextValues[key] = mapped.ids;
          nextValues[`${key}_label`] = mapped.labels;
          return;
        }

        if (
          code === "A3_MANUFACTURING_PLANT_DETAILS" &&
          isA3PlantMaterialQualityField(f, code)
        ) {
          applyA3PlantMaterialQualityValuesToForm(nextValues, {
            fieldName: f.name,
            sectionCode: code,
            rowIndex,
            materialQualityValues: row.materialQualityValues,
          });
          return;
        }

        const value = readField(code, f, row);
        if (value !== "") nextValues[key] = value;

        if (code === "A3_MANUFACTURING_PLANT_DETAILS" && f.type === "PLANT_CAPACITY") {
          const opt = row.plantCapacityOption as Record<string, unknown> | undefined;
          if (opt) {
            const cap = String(opt.capacityLabel ?? "").trim();
            if (cap) nextValues[`${key}_label`] = cap;
          }
        }

        if (
          code === "A3_PROCESS_DETAILS" &&
          f.name === "processName_A3_01" &&
          value !== ""
        ) {
          nextValues[`processName_A3_01_${code}_row${rowIndex}`] = value;
        }
      });

      if (code === "A3_EMBODIED_ENERGY_DETAILS") {
        energyTotal += Number(row.embodiedEnergy || 0);
      }
      if (code === "A3_EMBODIED_CARBON_DETAILS") {
        carbonTotal += Number(row.functionalUnitCarbon || 0);
      }
    });
  });

  return {
    values: nextValues,
    energyTotal: Math.round(energyTotal * 1000) / 1000,
    carbonTotal: Math.round(carbonTotal * 1000) / 1000,
  };
}
